/**
 * The browser-backed `PrefsCapability`: durable JSON storage for every host.
 * IndexedDB is the primary tier: unlike localStorage it can hold complete
 * conversation bodies and cached API payloads without the small synchronous
 * quota. A localStorage fallback keeps tests, older WebViews, and private-mode
 * failures functional.
 */

import axios from "axios";
import type { PrefsCapability, TenantIdentity } from "./Platform";
import { resourceAccessScope } from "../access/resourceAccess";
import { localScopeSlug } from "./tenantIdentity";

interface OfflineRecord {
    key: string;
    value: unknown;
    savedAt: number;
}

const DATABASE_NAME = "primepoint-native-offline";
const DATABASE_VERSION = 1;
const STORE_NAME = "json";
const FALLBACK_PREFIX = "primepointNativeOffline:";

let databasePromise: Promise<IDBDatabase | null> | null = null;
let operationTail: Promise<void> = Promise.resolve();
let storeGeneration = 0;
let acceptsOperations = true;

/**
 * Every cache operation captures this before its first asynchronous boundary.
 * Logout returns -1 while its clear is in flight, so work that starts during
 * the transition can never become valid merely because the clear later ends.
 */
export function captureNativeOfflineStoreGeneration(): number {
    return acceptsOperations ? storeGeneration : -1;
}

export function isNativeOfflineStoreGenerationCurrent(generation: number): boolean {
    return generation >= 0 && acceptsOperations && generation === storeGeneration;
}

function serializeOperation<T>(operation: () => Promise<T>): Promise<T> {
    const result = operationTail.then(operation, operation);
    operationTail = result.then(
        () => undefined,
        () => undefined,
    );
    return result;
}

function openDatabase(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === "undefined") return Promise.resolve(null);
    if (databasePromise) return databasePromise;

    databasePromise = new Promise((resolve) => {
        try {
            const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
            request.onupgradeneeded = () => {
                const database = request.result;
                if (!database.objectStoreNames.contains(STORE_NAME)) {
                    database.createObjectStore(STORE_NAME, { keyPath: "key" });
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => resolve(null);
            request.onblocked = () => resolve(null);
        } catch {
            resolve(null);
        }
    });
    return databasePromise;
}

function fallbackRead<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(FALLBACK_PREFIX + key);
        if (!raw) return null;
        return (JSON.parse(raw) as OfflineRecord).value as T;
    } catch {
        return null;
    }
}

function fallbackWrite(key: string, value: unknown): void {
    try {
        localStorage.setItem(
            FALLBACK_PREFIX + key,
            JSON.stringify({ key, value, savedAt: Date.now() } satisfies OfflineRecord),
        );
    } catch {
        // The cache is best-effort; IndexedDB is expected to be the normal tier.
    }
}

function fallbackRemove(key: string): boolean {
    try {
        localStorage.removeItem(FALLBACK_PREFIX + key);
        return true;
    } catch {
        // The IndexedDB tier can still be removed when localStorage is blocked.
        return false;
    }
}

async function readNativeOfflineValueInner<T>(
    key: string,
    expectedGeneration: number,
): Promise<T | null> {
    if (!isNativeOfflineStoreGenerationCurrent(expectedGeneration)) return null;
    const database = await openDatabase();
    if (!isNativeOfflineStoreGenerationCurrent(expectedGeneration)) return null;
    if (!database) return fallbackRead<T>(key);

    return new Promise((resolve) => {
        const currentFallback = (): T | null =>
            isNativeOfflineStoreGenerationCurrent(expectedGeneration) ? fallbackRead<T>(key) : null;
        try {
            const request = database
                .transaction(STORE_NAME, "readonly")
                .objectStore(STORE_NAME)
                .get(key);
            request.onsuccess = () => {
                if (!isNativeOfflineStoreGenerationCurrent(expectedGeneration)) {
                    resolve(null);
                    return;
                }
                const record = request.result as OfflineRecord | undefined;
                resolve(record ? (record.value as T) : currentFallback());
            };
            request.onerror = () => resolve(currentFallback());
        } catch {
            resolve(currentFallback());
        }
    });
}

export function readNativeOfflineValue<T>(
    key: string,
    expectedGeneration: number,
): Promise<T | null> {
    return serializeOperation(() => readNativeOfflineValueInner<T>(key, expectedGeneration));
}

async function writeNativeOfflineValueInner(
    key: string,
    value: unknown,
    expectedGeneration: number,
): Promise<void> {
    if (!isNativeOfflineStoreGenerationCurrent(expectedGeneration)) return;
    const record: OfflineRecord = { key, value, savedAt: Date.now() };
    const database = await openDatabase();
    if (!isNativeOfflineStoreGenerationCurrent(expectedGeneration)) return;
    if (!database) {
        fallbackWrite(key, value);
        return;
    }

    await new Promise<void>((resolve) => {
        const fallbackIfCurrent = () => {
            if (isNativeOfflineStoreGenerationCurrent(expectedGeneration)) {
                fallbackWrite(key, value);
            }
            resolve();
        };
        try {
            const transaction = database.transaction(STORE_NAME, "readwrite");
            transaction.objectStore(STORE_NAME).put(record);
            transaction.oncomplete = () => resolve();
            transaction.onerror = fallbackIfCurrent;
            transaction.onabort = fallbackIfCurrent;
        } catch {
            fallbackIfCurrent();
        }
    });
}

export function writeNativeOfflineValue(
    key: string,
    value: unknown,
    expectedGeneration: number,
): Promise<void> {
    return serializeOperation(() => writeNativeOfflineValueInner(key, value, expectedGeneration));
}

/**
 * Removes both durable representations without letting retired-session work
 * touch a new store. False means cleanup was incomplete and the caller must
 * retain any older source representation rather than risk losing its only copy.
 */
export function removeNativeOfflineValue(
    key: string,
    expectedGeneration: number,
): Promise<boolean> {
    return serializeOperation(async () => {
        if (!isNativeOfflineStoreGenerationCurrent(expectedGeneration)) return false;
        const database = await openDatabase();
        if (!isNativeOfflineStoreGenerationCurrent(expectedGeneration)) return false;
        if (!database) {
            return fallbackRemove(key);
        }

        return new Promise<boolean>((resolve) => {
            let settled = false;
            const settle = (removed: boolean) => {
                if (settled) return;
                settled = true;
                resolve(removed);
            };
            try {
                const transaction = database.transaction(STORE_NAME, "readwrite");
                transaction.objectStore(STORE_NAME).delete(key);
                transaction.oncomplete = () => {
                    if (!isNativeOfflineStoreGenerationCurrent(expectedGeneration)) {
                        settle(false);
                        return;
                    }
                    settle(fallbackRemove(key));
                };
                transaction.onerror = () => settle(false);
                transaction.onabort = () => settle(false);
            } catch {
                settle(false);
            }
        });
    });
}

function clearFallbackStore(): void {
    try {
        const keys = Array.from({ length: localStorage.length }, (_, index) =>
            localStorage.key(index),
        );
        for (const key of keys) {
            if (key?.startsWith(FALLBACK_PREFIX)) localStorage.removeItem(key);
        }
    } catch {
        // The IndexedDB tier is still cleared when localStorage is blocked.
    }
}

async function clearIndexedDBStore(): Promise<void> {
    const database = await openDatabase();
    if (database) {
        await new Promise<void>((resolve) => {
            try {
                const transaction = database.transaction(STORE_NAME, "readwrite");
                transaction.objectStore(STORE_NAME).clear();
                transaction.oncomplete = () => resolve();
                transaction.onerror = () => resolve();
                transaction.onabort = () => resolve();
            } catch {
                resolve();
            }
        });
        try {
            database.close();
        } catch {
            // A failed close does not undo the completed clear transaction.
        }
    }
    databasePromise = null;

    if (typeof indexedDB === "undefined") return;
    await new Promise<void>((resolve) => {
        try {
            const request = indexedDB.deleteDatabase(DATABASE_NAME);
            request.onsuccess = () => resolve();
            request.onerror = () => resolve();
            // The object store was already cleared above. Do not hang logout
            // if another same-origin page temporarily keeps a connection open.
            request.onblocked = () => resolve();
        } catch {
            resolve();
        }
    });
}

/**
 * Invalidates synchronously, then drains all already-started reads/writes and
 * clears behind them. New callers cannot capture a usable generation until
 * this promise settles, so same-org relogin cannot observe or recreate the
 * outgoing member's cache.
 */
export function clearNativeOfflineStore(): Promise<void> {
    storeGeneration += 1;
    acceptsOperations = false;
    const clearGeneration = storeGeneration;

    return serializeOperation(async () => {
        clearFallbackStore();
        await clearIndexedDBStore();
    }).finally(() => {
        if (storeGeneration === clearGeneration) acceptsOperations = true;
    });
}

/**
 * The API origin is part of each durable tenant key. Axios is configured
 * before native requests start. The origin the store was built with is the
 * fallback for consumers that do not use axios.
 */
function currentApiOrigin(apiOrigin: () => string): string {
    const configuredBaseUrl = axios.defaults.baseURL;
    if (typeof configuredBaseUrl === "string" && configuredBaseUrl.length > 0) {
        return configuredBaseUrl;
    }
    return apiOrigin();
}

/**
 * Prefs are a member of one platform. They read that platform's tenant and
 * bootstrap origin through the values they were built with, never through the
 * installed-platform ref, so they work before and without `installPlatform`.
 */
export function createBrowserPrefs(
    tenant: TenantIdentity,
    apiOrigin: () => string,
): PrefsCapability {
    // The same org slug can exist in local, staging, and production. IndexedDB
    // survives environment reloads, so origin is part of the durable namespace
    // just as it is in the native ConversationsApi disk-cache metadata.
    function durableKey(namespace: string, tenantSlug: string | null, suffix: string) {
        if (!tenantSlug) return null;
        const origin = encodeURIComponent(currentApiOrigin(apiOrigin));
        return `${namespace}:${tenantSlug}:${origin}:${suffix}`;
    }
    return {
        captureGeneration: captureNativeOfflineStoreGeneration,
        isGenerationCurrent: isNativeOfflineStoreGenerationCurrent,
        read: readNativeOfflineValue,
        write: writeNativeOfflineValue,
        remove: removeNativeOfflineValue,
        clear: clearNativeOfflineStore,
        tenantKey(namespace, suffix) {
            // Server content depends on who reads it: the key names the admitted reader.
            const reader = resourceAccessScope();
            return reader
                ? durableKey(namespace, tenant.orgSlug(), `${encodeURIComponent(reader)}:${suffix}`)
                : null;
        },
        retiredTenantKey(namespace, suffix) {
            return durableKey(namespace, tenant.orgSlug(), suffix);
        },
        preferenceKey(namespace, suffix) {
            return durableKey(namespace, localScopeSlug(tenant.orgSlug()), suffix);
        },
    };
}
